/**
 * E6 -- a control as a reader sees it, rather than as a grader edits it.
 *
 * An auditor opening a control got the preparer's editing panel: a
 * status dropdown, maturity spinners and free-text boxes he had no
 * authority to use, and none of what he came for -- when it was graded,
 * what the judgement says, and what evidence stands behind it.
 *
 * The grader is named because the API now resolves the id to a name;
 * where it resolves to nobody the line is left out rather than showing
 * an id, which is the defect E5 removed one surface over.
 *
 * "Covering documents" are the bank's own policy mapped to this control
 * (P278), which is a different thing from the evidence attached to the
 * assessment: the evidence is what the grader cited, the documents are
 * what the bank has. An auditor asking what stands behind a grade wants
 * both, and only the first was here.
 */
import type { DocumentReadingCounts } from "shared/api/types";
import { ConfirmedVerb } from "shared/components/ConfirmedVerb";
import { DocumentReadingNote } from "shared/documents/DocumentReadingNote";
import { useQuery } from "@tanstack/react-query";
import { stateLabel, statusLabel } from "shared/vocab/workflowState";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { api } from "shared/api/client";
import { useUpsertAssessment } from "framework/api/assessment_hooks";
import { useWithdrawCoveringDocument } from "framework/api/useDocumentGap";
import { ControlTestRecord } from "framework/assessments/ControlTestRecord";
import { useMe } from "shared/api/useMe";
import { clearAcceptance, useAcceptance } from "framework/assessments/engineAcceptance";

import type {
  EvidenceArtefact,
  FrameworkAssessment,
  FrameworkControl,
} from "shared/api/types";
import { formatDate, formatDateTime } from "shared/time/formatDate";

function Line({ label, value }: { label: string; value: string | null }) {
  // An empty field is left out rather than shown blank: a row of empty
  // labels reads as a broken panel, not as an ungraded control.
  if (!value || !value.trim()) return null;
  return (
    <div className="text-xs">
      <dt className="text-slate-500">{label}</dt>
      <dd className="whitespace-pre-wrap text-slate-800">{value}</dd>
    </div>
  );
}

interface CoveringDocument extends DocumentReadingCounts {
  document_id: string;
  filename: string | null;
  title: string | null;
}

/** Round 14 A3 -- the seats that may reject a mapping may withdraw one. */
const MAPPING_SEATS = ["preparer", "reviewer", "approver"];

export function ControlReviewPanel({
  control,
  live,
  evidence,
  cycleId,
}: {
  control: FrameworkControl;
  live: FrameworkAssessment | null | undefined;
  evidence: EvidenceArtefact[];
  /** Round 10 C5: needed to put a judgement back after the toast has gone. */
  cycleId?: string;
}) {
  const { t, i18n } = useTranslation();
  const accepted = useAcceptance(control.id);
  const undo = useUpsertAssessment(cycleId ?? "");
  const covering = useQuery<CoveringDocument[]>({
    queryKey: ["control", control.id, "covering-documents"],
    queryFn: () =>
      api.get<CoveringDocument[]>(
        `/v1/framework/controls/${control.id}/covering-documents`,
      ),
    retry: false,
  });
  const me = useMe();
  const withdraw = useWithdrawCoveringDocument(control.id);
  // Read defensively: a partial /me must not take the panel down.
  const positions = Array.isArray(me.data?.positions) ? me.data.positions : [];
  // Round 17 A29 -- the evidence is frozen while the control is in
  // review: the reviewer was offered "Withdraw" under a queue telling
  // him he does not edit it, and the preparer changed the documents
  // under him. The server refuses too.
  const underReview =
    live?.workflow_state === "in_review" || live?.workflow_state === "in_signoff";
  const canWithdraw =
    !underReview &&
    (me.data?.is_admin === true || positions.some((p) => MAPPING_SEATS.includes(p)));

  if (!live) {
    return (
      <p
        data-testid="control-review-ungraded"
        className="rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600"
      >
        {t("assessmentGrid.review.ungraded")}
      </p>
    );
  }

  return (
    <section
      data-testid="control-review-panel"
      className="space-y-3 rounded border border-slate-200 p-3"
    >
      <div className="flex flex-wrap items-baseline gap-2">
        <span
          data-testid="control-review-status"
          className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-semibold text-slate-800"
        >
          {/* B10 -- the reader's panel printed the database's own word.
              "no_evidence" is a column value, not a judgement a person
              would write down. */}
          {statusLabel(live.status)}
        </span>
        <span className="text-xs text-slate-500">
          {t("assessmentGrid.review.maturity", {
            current: live.current_ml ?? "—",
            target: live.target_ml,
          })}
        </span>
        {/* Round 12 F5 -- an accepted suggestion set a current maturity
            ABOVE the cycle's target, printed as "maturity 4 of 3" in
            the drawer and "COMPLIANT 4 / 3" on the grid row a reviewer
            reads before he opens anything, with no clamp, no warning
            and no sentence saying whether that is legitimate.

            NOT clamped: exceeding a target is a real thing a bank can
            do, and silently lowering the Engine's number would be
            worse than printing it. What was missing is the sentence. */}
        {live.current_ml != null && live.current_ml > live.target_ml && (
          <span
            data-testid="control-review-above-target"
            className="text-xs font-medium text-amber-800"
          >
            {t("assessmentGrid.review.aboveTarget", {
              current: live.current_ml,
              target: live.target_ml,
            })}
          </span>
        )}
        <span data-testid="control-review-when" className="text-xs text-slate-500">
          {t("assessmentGrid.review.graded", {
            when: formatDateTime(live.reviewed_at ?? live.updated_at, i18n.language),
          })}
        </span>
        {live.workflow_state && (
          /* D4 (round 8) -- the raw column value. `in_review` is our
             schema, printed to somebody auditing a control; the
             catalogue has named these since round 2 and this one
             surface was never routed through it. */
          <span className="text-xs text-slate-500">
            {stateLabel(live.workflow_state)}
          </span>
        )}
      </div>

      <dl className="space-y-2">
        <Line
          label={t("assessmentGrid.review.requirement")}
          value={control.control_text}
        />
        <Line
          label={t("assessmentGrid.evidenceSummary")}
          value={live.evidence_summary}
        />
        <Line label={t("assessmentGrid.gaps")} value={live.gaps} />
        <Line
          label={t("assessmentGrid.review.recommendations")}
          value={live.recommendations}
        />
        <Line label={t("assessmentGrid.review.notes")} value={live.notes} />
      </dl>

      {/* D2 -- "Graded by" was printing the ASSIGNEE, which is who the
          work was given to and frequently nobody. A reader auditing a
          control is asking whose judgement this is, and the seal is the
          only record that names the author. Both are shown now, because
          they are different facts and a reviewer wants each. */}
      {(live.graded_by_username ||
        live.assignee_username ||
        live.approver_username) && (
        <p data-testid="control-review-people" className="text-xs text-slate-500">
          {/* D4 -- and WHEN: the seal's own timestamp, not the row's.
              Round 17 A29 -- and who MOVED it, apart from who graded
              it: a send-back read "Graded by Rashid Reviewer", who
              graded nothing. One sentence, so no ". on" glue (A32). */}
          {live.graded_by_username && (
            <span data-testid="control-review-graded">
              {t("assessmentGrid.review.gradedByWho", { who: live.graded_by_username })}
              {live.graded_at &&
                t("assessmentGrid.review.gradedOnWhen", {
                  when: formatDate(live.graded_at, i18n.language),
                })}
              {live.moved_by_username &&
                live.moved_action &&
                t(`assessmentGrid.review.moved.${live.moved_action}`, {
                  who: live.moved_by_username,
                  defaultValue: "",
                })}
              {". "}
            </span>
          )}
          {live.assignee_username &&
            t("assessmentGrid.review.assignedTo", { who: live.assignee_username })}
          {live.approver_username &&
            t("assessmentGrid.review.approvedBy", { who: live.approver_username })}
          {/* D8 (round 8) -- and whether the Engine proposed it.
              Third round. The drawer said "Graded by Dana Preparer."
              and nothing more, on a judgement the Engine had drafted
              and she had confirmed. For a product whose claim is AI an
              examiner can trust, "who decided this" is the whole
              question, and a person confirming a machine's proposal is
              a different provenance from a person forming a view. The
              column has recorded it since the accept started writing
              it; nothing read it back. */}
          {live.derived_from_run_id && (
            <span data-testid="control-review-engine-assisted">
              {t("assessmentGrid.review.engineAssisted")}
            </span>
          )}
        </p>
      )}

      {/* Round 10 C5 -- the Engine's hand, and the way back, after the
          toast has gone. Nine seconds after accepting there was no Undo
          anywhere and the record said only who graded it, above three
          paragraphs she did not write. */}
      {accepted && (
        <p
          data-testid="control-review-engine-acceptance"
          className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-900"
        >
          {t("assessmentGrid.review.engineAccepted", {
            who: accepted.acceptedBy,
            when: formatDateTime(accepted.acceptedAt, i18n.language),
          })}
          {accepted.previous && (
            <button
              type="button"
              data-testid="control-review-engine-undo"
              disabled={undo.isPending}
              onClick={() => {
                const prev = accepted.previous;
                if (!prev || !cycleId) return;
                void undo
                  .mutateAsync({
                    controlId: control.id,
                    body: {
                      status: prev.status as FrameworkAssessment["status"],
                      current_ml: prev.current_ml,
                      evidence_summary: prev.evidence_summary,
                      gaps: prev.gaps,
                      recommendations: prev.recommendations,
                      // Restoring a human judgement must take the
                      // Engine's name off it too, or the record
                      // attributes to the Engine a grade it did not set.
                      clear_derived_from_run: true,
                    },
                  })
                  .then(() => clearAcceptance(control.id));
              }}
              className="ms-2 font-semibold underline decoration-dotted underline-offset-2 disabled:opacity-50"
            >
              {t("assessmentGrid.review.engineUndo")}
            </button>
          )}
        </p>
      )}

      <div>
        <p className="text-xs text-slate-500">
          {t("assessmentGrid.review.evidenceHeading", { count: evidence.length })}
        </p>
        {evidence.length > 0 && (
          <ul data-testid="control-review-evidence" className="mt-1 space-y-1">
            {evidence.map((e) => (
              <li key={e.link_id} className="text-xs text-slate-700">
                <span className="text-slate-900">{e.filename ?? e.uri}</span>
                {e.uploader_email && (
                  <span className="text-slate-500">
                    {t("assessmentGrid.review.attachedBy", { who: e.uploader_email })}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* D11 -- when nothing covered the control this section vanished,
          so "what policy stands behind this grade?" was answered by an
          absence the reader could not distinguish from a section that
          had not loaded. Nothing mapped is a real answer, and the gap
          report is where it gets fixed. */}
      {/* ROUND 14 A6 -- the tests, their authors and what they rested
          on, for every seat. The reviewer's drawer had none of it. */}
      {cycleId && <ControlTestRecord cycleId={cycleId} controlId={control.id} />}

      {covering.data && covering.data.length === 0 && (
        <p data-testid="control-review-no-documents" className="text-xs text-slate-500">
          {t("assessmentGrid.review.noDocuments")}{" "}
          {/* D4 (round 8) -- the sentence NAMED the gap report and left
              the reader to find it. Fifth round; the route has existed
              since P278. */}
          <Link
            to="/framework/document-gap"
            data-testid="control-review-gap-link"
            className="text-brand-brassDark hover:underline"
          >
            {t("assessmentGrid.review.openDocumentGap")}
          </Link>
        </p>
      )}
      {covering.data && covering.data.length > 0 && (
        <div>
          <p className="text-xs text-slate-500">
            {t("assessmentGrid.review.documentsHeading", {
              count: covering.data.length,
            })}
          </p>
          {underReview && (
            <p
              data-testid="control-review-documents-frozen"
              className="text-xs text-slate-500"
            >
              {t("assessmentGrid.review.documentsFrozen")}
            </p>
          )}
          <ul data-testid="control-review-documents" className="mt-1 space-y-1">
            {covering.data.map((d) => (
              <li key={d.document_id} className="text-xs text-slate-700">
                <bdi>{d.title || d.filename}</bdi>
                <DocumentReadingNote
                  reading={d}
                  testId={`control-review-reading-${d.document_id}`}
                />
                {/* ROUND 14 A3 -- the drawer where a mapping is made
                    offered no way back. */}
                {canWithdraw && (
                  <>
                    {" · "}
                    <ConfirmedVerb
                      testId={`control-review-withdraw-${d.document_id}`}
                      disabled={withdraw.isPending}
                      label={t("assessmentGrid.review.withdrawDocument")}
                      prompt={t("assessmentGrid.review.withdrawDocumentAsk", {
                        name: d.title || d.filename,
                      })}
                      confirmLabel={t("assessmentGrid.review.withdrawDocumentConfirm")}
                      onConfirm={() => withdraw.mutate({ documentId: d.document_id })}
                    />
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
